import { useQuery } from "@tanstack/react-query";
import type { KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { balances, type BalanceSummary } from "@/app/billing";
import { ClearFilters } from "@/app/ClearFilters";
import { accounts as accountRes, customers as customerRes } from "@/app/master";
import { Pager } from "@/app/Pager";
import { accountOptions, idOptions, loadingOf, useList, useNames } from "@/app/queries";
import { useSearchFilters } from "@/app/useSearchFilters";
import { usePage } from "@/app/usePage";
import { Alert, Combobox, EmptyState, FILTER_W, Money, PageHeader, Table, TableWrap, Th, Toolbar } from "@/ui";

/** 账号余额：列出全部账号的余额与今天、昨天、本月的消耗（北京日，决策 D99），可按客户、账号筛选，点行进入账号的按日增减与充值（docs/04 第 4 节）。
 * 筛选条件在地址栏里，从详情返回时还在。 */
export function Balances() {
  const navigate = useNavigate();
  const location = useLocation();
  const names = useNames();
  const accountList = useList("accounts", accountRes);
  const customerList = useList("customers", customerRes);
  const { filters, set, clear, active } = useSearchFilters(["customer", "account"]);
  const q = useQuery({ queryKey: ["balances"], queryFn: balances.list });
  const rows = (q.data ?? []).filter((r) => (!filters.customer || String(r.customer_id) === filters.customer) && (!filters.account || String(r.account_id) === filters.account));
  const paged = usePage("balances", rows, filters);
  // 选了客户时账号下拉只列这个客户的账号
  const ownAccounts = (accountList.data ?? []).filter((a) => !filters.customer || String(a.customer_id) === filters.customer);
  const open = (r: BalanceSummary) => navigate(`/balances/${r.account_id}`, { state: { back: location.search } });

  return (
    <>
      <PageHeader title="账号余额" description="余额是流水账余额，消耗是扣费合计，按北京日统计；价格更正与调整不计入消耗。点一行看每天的增减。" />
      {q.error && <Alert tone="danger" title="加载失败">{q.error.message}</Alert>}
      <Toolbar actions={active && <ClearFilters onClear={clear} />}>
        <Combobox size="sm" width={FILTER_W.select} ariaLabel="客户" searchPlaceholder="搜索客户名称" options={[{ value: "", label: "全部客户" }, ...idOptions(customerList.data)]} value={filters.customer} onChange={(v) => set("customer", v)} />
        <Combobox size="sm" width={FILTER_W.select} ariaLabel="账号" searchPlaceholder="搜索账号名称或 system_id" options={[{ value: "", label: "全部账号" }, ...accountOptions(ownAccounts, customerList.data)]} value={filters.account} onChange={(v) => set("account", v)} />
      </Toolbar>
      <TableWrap loading={loadingOf(q)}>
        {paged.rows.length ? (
          <>
            <Table>
              <thead><tr><Th w={240}>账号</Th><Th w="name">客户</Th><Th numeric w={176}>余额</Th><Th numeric w={176}>今天消耗</Th><Th numeric w={176}>昨日消耗</Th><Th numeric>本月消耗</Th></tr></thead>
              <tbody>
                {paged.rows.map((r) => (
                  <tr key={r.account_id} className="is-clickable" tabIndex={0} onClick={() => open(r)} onKeyDown={(e: KeyboardEvent) => e.key === "Enter" && open(r)}>
                    <td>{names.account(r.account_id)} <span className="ax-secondary">#{r.account_id}</span></td>
                    <td>{names.customer(r.customer_id)}</td>
                    <td className="ax-num"><Money micro={r.balance} currency={r.currency} /></td>
                    <td className="ax-num"><Money micro={r.today} currency={r.currency} /></td>
                    <td className="ax-num"><Money micro={r.yesterday} currency={r.currency} /></td>
                    <td className="ax-num"><Money micro={r.month} currency={r.currency} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...paged} />
          </>
        ) : (
          !q.isPending && <EmptyState title={active ? "没有符合筛选条件的账号" : "还没有账号"}>{active ? "" : "在“客户账号”新建账号后，这里列出它的余额。"}</EmptyState>
        )}
      </TableWrap>
    </>
  );
}
